import React, { useCallback, useEffect, useState } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { sendConfirmationToCS } from 'api/notification' // Ensure this path is correct
import config from 'constants/config' // Ensure this path is correct

const StyledGroup = styled(Group)`
  min-height: ${rem(360)};
`

const StyledLayoutContainer = styled(LayoutContainer)`
  background-color: white;
`

interface Traveller {
  dob: string;
  n: string;
  pN?: string;
  pE?: string;
  t: string;
  qff?: string;
  vff?: string;
}

interface MappedStateProps {
  search?: string;
}

function DetailsCheckConfirmationPage({ search }: MappedStateProps) {
  const [errorMessage, setErrorMessage] = useState('')

  const fetchData = useCallback(async() => {
    setErrorMessage('')
    if (search) {
      const params = new URLSearchParams(search)
      const name = params.get('name')
      const email = params.get('email')
      const spooferEmail = params.get('sE')
      const cut = params.get('cut')

      if (!name || !email || !spooferEmail) {
        setErrorMessage('name, email and spooferEmail are required in the URL search params.')
        return
      }

      const dobs = params.getAll('dob')
      const names = params.getAll('n')
      const types = params.getAll('t')
      const passNums = params.getAll('pN')
      const passExps = params.getAll('pE')
      const qantasFF = params.getAll('qff')
      const velocityFF = params.getAll('vff')

      // Validate required traveller fields
      if (!dobs.length || !names.length || !types.length || dobs.length !== names.length || dobs.length !== types.length) {
        setErrorMessage('Each traveller must have a dob, name, and type.')
        return
      }

      const decodedName = decodeURIComponent(name)
      const decodedEmail = decodeURIComponent(email)
      const decodedSpooferEmail = decodeURIComponent(spooferEmail)

      const constructedTravellers: Traveller[] = dobs.map((dob, index) => ({
        dob: decodeURIComponent(dob),
        n: decodeURIComponent(names[index]).replace(/%20/g, ' '),
        t: decodeURIComponent(types[index]),
        pN: passNums[index] ? decodeURIComponent(passNums[index]) : undefined,
        pE: passExps[index] ? decodeURIComponent(passExps[index]) : undefined,
        qff: qantasFF[index] ? decodeURIComponent(qantasFF[index]) : undefined,
        vff: velocityFF[index] ? decodeURIComponent(velocityFF[index]) : undefined,
      }))

      const body = {
        brand: config.BRAND,
        travellers: constructedTravellers,
        name: decodedName,
        email: decodedEmail,
        sE: decodedSpooferEmail,
        cut: cut ?? undefined,
      }

      try {
        await sendConfirmationToCS(body)
      } catch (e) {
        if (e.error) {
          setErrorMessage(e.error)
        } else if (e.message) {
          setErrorMessage(e.message)
        }
      }
    }
  }, [search])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return (
    <StyledLayoutContainer size="full">
      <StyledGroup direction="vertical" horizontalAlign="center" verticalAlign="center" gap={24}>
        <Heading variant="heading3" align="center" as="h1">Thank you for confirming your traveller details</Heading>
        <BodyText variant="medium" align="center">Your customer sales agent will now finalise the booking. You will receive an email confirmation once the payment has been made.</BodyText>
        {errorMessage.length > 0 && <BodyText variant="medium" colour="critical">Error: {errorMessage}</BodyText>}
      </StyledGroup>
    </StyledLayoutContainer>
  )
}

function mapStateToProps(state: any): MappedStateProps {
  return {
    search: state.router.location.search,
  }
}

export default connect(mapStateToProps)(DetailsCheckConfirmationPage)
